import { z } from 'zod';
import {
  ARENA_SHAPES,
  BOT_TIERS,
  CHARACTER_IDS,
  GAME_MODES,
  MAP_IDS,
  MAP_MODES,
  MAP_PROPS_MODES,
  OBSTACLE_DENSITIES,
  RUNE_IDS,
  SLOT_KEYS,
  SPELL_IDS,
} from '../types';
import { FRICTION_PRESET_IDS } from '../data/friction';
import { MAX_PLAYERS, WORLD_LIMIT } from '../physics/constants';
import { MOVE_DIR_MAX } from '../physics/integrate';

export const NAME_MAX_LENGTH = 16;

const coord = z.number().min(-WORLD_LIMIT).max(WORLD_LIMIT);

/**
 * Trim, drop control/format characters and collapse whitespace. Returns an empty string when
 * nothing printable is left, so callers can fall back to a default name.
 */
export function sanitizeName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/[\p{C}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NAME_MAX_LENGTH)
    .trim();
}

export const JoinOptionsSchema = z.object({
  name: z.string().max(64).optional(),
  /**
   * Deliberately a loose string, not an enum: the server resolves it with `resolveCharacter`.
   * An enum would make a stale stored id fail the whole parse and drop the player's name too.
   */
  character: z.string().max(32).optional(),
  /** Single-use seat ticket minted by the room for a bot; humans never send one. */
  botTicket: z.string().max(64).optional(),
});
export type JoinOptions = z.infer<typeof JoinOptionsSchema>;

export const CreateOptionsSchema = JoinOptionsSchema.extend({
  private: z.boolean().optional(),
});
export type CreateOptions = z.infer<typeof CreateOptionsSchema>;

export const ReadyMsg = z.strictObject({ ready: z.boolean() });
export type ReadyMsg = z.infer<typeof ReadyMsg>;

export const StartMsg = z.strictObject({}).optional();
export type StartMsg = z.infer<typeof StartMsg>;

/** Host settings patch; values are range-clamped afterwards by `clampSettings`. */
export const SettingsMsg = z
  .strictObject({
    mode: z.enum(GAME_MODES),
    rounds: z.number(),
    startingGold: z.number(),
    goldPerRound: z.number(),
    shopSeconds: z.number(),
    arenaShape: z.enum(ARENA_SHAPES),
    startRadius: z.number(),
    graceMs: z.number(),
    shrinkIntervalMs: z.number(),
    shrinkPct: z.number(),
    lavaDpsPct: z.number(),
    frictionPreset: z.enum(FRICTION_PRESET_IDS),
    knockbackMult: z.number(),
    mapMode: z.enum(MAP_MODES),
    /** Deduped and put in canonical order by `clampSettings`; empty keeps the previous pool. */
    mapPool: z.array(z.enum(MAP_IDS)).max(MAP_IDS.length),
    startMapId: z.enum(MAP_IDS),
    mapProps: z.enum(MAP_PROPS_MODES),
    racialAbilities: z.boolean(),
    obstacleDensity: z.enum(OBSTACLE_DENSITIES),
  })
  .partial();
export type SettingsMsg = z.infer<typeof SettingsMsg>;

/** Lobby character pick. Clients only send catalog ids, so anything else is rejected. */
export const CharacterMsg = z.strictObject({ character: z.enum(CHARACTER_IDS) });
export type CharacterMsg = z.infer<typeof CharacterMsg>;

const seatId = z.string().min(1).max(32);

/**
 * Host adds `count` bots of one tier in the lobby. "Fill" sends target − seats; the server clamps
 * to the free seats.
 */
export const AddBotMsg = z.strictObject({
  tier: z.enum(BOT_TIERS),
  count: z
    .int()
    .min(1)
    .max(MAX_PLAYERS - 1),
});
export type AddBotMsg = z.infer<typeof AddBotMsg>;

/** Host removes a bot seat. The server rejects targets that are not bots. */
export const RemoveBotMsg = z.strictObject({ sessionId: seatId });
export type RemoveBotMsg = z.infer<typeof RemoveBotMsg>;

/** Host changes a bot seat's tier. The server rejects targets that are not bots. */
export const BotTierMsg = z.strictObject({ sessionId: seatId, tier: z.enum(BOT_TIERS) });
export type BotTierMsg = z.infer<typeof BotTierMsg>;

export const CastMsg = z.strictObject({
  slot: z.enum(SLOT_KEYS),
  x: coord,
  y: coord,
  seq: z.int().nonnegative(),
});
export type CastMsg = z.infer<typeof CastMsg>;

/** The active racial (Stone Guard) toward the cursor; the server derives the guard facing. */
export const RacialMsg = z.strictObject({
  x: coord,
  y: coord,
  seq: z.int().nonnegative(),
});
export type RacialMsg = z.infer<typeof RacialMsg>;

/**
 * Shop phase purchases and ready-up. Only ids and indices come from the client; prices, gold and
 * slot rules are enforced by `applyShopAction` on the server.
 */
export const ShopMsg = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('buySpell'), spellId: z.enum(SPELL_IDS) }),
  z.strictObject({ type: z.literal('upgrade'), slot: z.enum(SLOT_KEYS) }),
  z.strictObject({ type: z.literal('sellSpell'), slot: z.enum(SLOT_KEYS) }),
  z.strictObject({
    type: z.literal('unlockRune'),
    slot: z.enum(SLOT_KEYS),
    runeId: z.enum(RUNE_IDS),
  }),
  /** Free; `null` clears the active rune. Only in the Shop phase, like every shop message. */
  z.strictObject({
    type: z.literal('setRune'),
    slot: z.enum(SLOT_KEYS),
    runeId: z.enum(RUNE_IDS).nullable(),
  }),
  z.strictObject({ type: z.literal('shopReady'), ready: z.boolean() }),
]);
export type ShopMsg = z.infer<typeof ShopMsg>;

/** Clamp ranges for the continuous movement input channel. */
export const MOVE_INPUT_LIMITS = {
  moveX: [-WORLD_LIMIT, WORLD_LIMIT],
  moveY: [-WORLD_LIMIT, WORLD_LIMIT],
  moveDir: [0, MOVE_DIR_MAX],
} as const;
